import { useEffect } from "react";
import { Box, Dialog, DialogTitle, DialogContent, Button } from "@mui/material";

import { ConfirmationModalProps } from "./ConfirmationModal.types";
import ButtonsFooter from "../../buttons/ButtonsFooter/ButtonsFooter";
import { sxStyles } from "./ConfirmationModal.styles";
import IMAGES from "lib/assets/images";
import { IconButton } from "@mui/material";

import {
  disableBackButton,
  enableBackButton,
} from "lib/routing/navigate/navigate";
import theme from "lib/theme/theme";

/* A function that returns a Dialog component. Disables android physical back button when open and reenables back button on close. 
Dev: take note that if the page has disableBackButton, then you will need to pass in disableBackButton again when the modal is closed */
const ConfirmationModal = ({
  title,
  titleBold = true,
  isNextButtonDisabled = false,
  body,
  hideNextButton = false,
  nextButtonText,
  nextButtonLabel,
  hideCancelButton = false,
  cancelButtonText,
  cancelButtonLabel,
  hideAdditionalButton = true,
  additionalButtonText,
  additionalButtonLabel,
  open,
  enableBGBlur = false,
  position = "center",
  onClose,
  onClickNext,
  onClickCancel,
  onClickAdditional,
  showCloseButton = false,
}: ConfirmationModalProps) => {
  const classes = sxStyles({ position: position, enableBGBlur: enableBGBlur });

  useEffect(() => {
    if (open) {
      disableBackButton();
    }
  }, [open]);

  const handleClickNext = () => {
    if (onClickNext) {
      enableBackButton();
      onClickNext();
    }
  };

  const handleClickCancel = () => {
    if (onClickCancel) {
      enableBackButton();
      onClickCancel();
    }
  };

  const handleClickAdditional = () => {
    if (onClickAdditional) {
      enableBackButton();
      onClickAdditional();
    }
  };

  const handleClose = () => {
    enableBackButton();
    if (onClose) onClose();
  };

  return (
    <Dialog
      fullWidth={true}
      maxWidth="lg"
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          if (onClose) onClose();
        }
      }}
      sx={classes.dialogContainer}
    >
      <Box sx={classes.dialog}>
        {showCloseButton && (
          <IconButton
            aria-label="closeIcon"
            sx={classes.closeButton}
            onClick={handleClose}
          >
            <Box component={"img"} src={IMAGES.general.GeneralCloseIcon} />
          </IconButton>
        )}

        {title && (
          <DialogTitle sx={classes.dialogTitle}>
            <Box sx={classes.dialogTitleContent}>
              <Box
                sx={{
                  ...classes.title,
                  fontWeight: titleBold
                    ? theme.typography.fontWeightMedium
                    : theme.typography.fontWeightRegular,
                }}
              >
                {title}
              </Box>
            </Box>
          </DialogTitle>
        )}
        <DialogContent sx={classes.dialogContent}>{body}</DialogContent>
        <Box m={2}>
          <ButtonsFooter
            isDisabled={isNextButtonDisabled}
            hideNextButton={hideNextButton}
            nextButtonText={nextButtonText || ""}
            nextButtonLabel={nextButtonLabel}
            hideCancelButton={hideCancelButton}
            cancelButtonText={cancelButtonText || ""}
            cancelButtonLabel={cancelButtonLabel}
            onClickNext={handleClickNext}
            onClickCancel={handleClickCancel}
          />
        </Box>

        {!hideAdditionalButton && (
          <Box m={2}>
            <Button
              variant="outlined"
              color="primary"
              fullWidth={true}
              onClick={handleClickAdditional}
              data-testid="additionalButton"
              // classes={{ label: classes.additionalButtonText }}
            >
              {additionalButtonText}
            </Button>
            {additionalButtonLabel ? (
              <Box sx={classes.additionalButtonLabel}>
                {additionalButtonLabel}
              </Box>
            ) : null}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default ConfirmationModal;
